import Image from "next/image";
import { IoIosArrowForward } from "react-icons/io";
import { motion, Variants } from "framer-motion";
import { useLanguage } from "@/components/context/LanguageContext";
import Portofolio from "@/components/portofolio";
import Head from "next/head";
import Link from "next/link";
import useSWR from "swr";
import type { Projects } from "@/types/project";


const cardVariant: Variants = {
    hidden: {
        opacity: 0,
        y: 30,
        scale: 0.95,
    },
    visible: (i: number) => ({
        opacity: 1,
        y: 0,
        scale: 1,
        transition: {
            duration: 0.6,
            delay: i * 0.2,
            ease: "easeOut",
        },
    }),
};

const Portfolio = () => {
    const { language } = useLanguage();
    const fetcher = (url: string) => fetch(url).then(res => res.json());
    const { data, error, isLoading } = useSWR('/api/project', fetcher, { suspense: false });
    const projects: Projects[] = data?.projects || [];
    return (
        <>
            <Head>
                <title>Profile | Portfolio</title>
                <meta name="description" content="Generated by create next app" />
                <meta name="viewport" content="width=device-width, initial-scale=1" />
            </Head>

            <div className="my-22">
                <div className="relative w-full h-[300px] border">
                    <Image
                        src="/gambar4.jpg"
                        alt="Group Photo"
                        fill
                        className=" object-cover object-center"
                    />
                    <div className="absolute top-1/2 left-0 transform -translate-y-1/2 space-y-6 px-4 bg-gradient-to-r bg-transparent from-white/85 via-[#f0fdf4]/65 to-white/1 w-[75%] h-[300px] p-4 ">
                        <div className="space-y-20 xl:ml-15 items-center justify-center xl:justify-start">
                            <div className="ml-15 flex items-center space-x-2">
                                <p className="text-sm text-gray-800">Home</p>
                                <IoIosArrowForward className="text-gray-800 text-xl" />
                                <p className="text-sm text-gray-800">Profile</p>
                                <IoIosArrowForward className="text-gray-800 text-xl" />
                                <p className="text-sm text-gray-800">About</p>
                            </div>
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.8 }}
                                className="">
                                <p className="text-3xl xl:text-4xl font-bold  bg-clip-text bg-gradient-to-r text-gray-700 ml-15">
                                    {language === "en" ? "Portfolio" : "Portfolio"}
                                </p>
                            </motion.div>
                        </div>
                    </div>
                </div>
                <motion.div
                    initial={{ y: 100, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: 100, opacity: 0 }}
                    transition={{ duration: 0.5, delay: 0.2 }}>
                    <Portofolio />
                </motion.div>
                <div className="max-w-screen-xl mx-auto px-4 py-10 bg-white">
                    <div className={`grid ${projects.length > 1 ? "md:grid-cols-2 xl:grid-cols-3" : "grid-cols-1"} gap-6 mb-8 `}>
                        {projects.map((project, index) => (
                            <motion.div
                                key={project.id}
                                custom={index}
                                variants={cardVariant}
                                initial="hidden"
                                whileInView="visible"
                                whileHover={{ scale: 1.03 }}
                                whileTap={{ scale: 0.98 }}
                                viewport={{ once: true, amount: 0.3 }}
                                className="max-w-xl mx-auto rounded-xl overflow-hidden shadow-md bg-white flex flex-col justify-between relative"
                            >

                                <Image
                                    className="w-full rounded-md"
                                    src={project.headerImage}
                                    alt='Project Image'
                                    width={600}
                                    height={400}
                                />
                                <p className="absolute top-2 rounded-xl right-2 text-xs text-white bg-green-600/80 p-2 text-center">{project.status}</p>
                                <div className="px-6 py-4">
                                    <h3 className="font-semibold text-xl mb-1 text-center text-gray-800">
                                        {language === "en" ? project.title.en : project.title.id}
                                    </h3>
                                    {/* <p className="text-gray-700 text-base text-center mb-4">(100 MWp)</p> */}
                                </div>
                                <div className="w-full bg-gray-100 text-md font-medium text-[#005b96] text-center p-4 underline hover:bg-gray-200 transition">
                                    {project.content.length > 0 ? (
                                        <Link
                                            href={`/profile/portfolio/${project.id}`} className="disabled:cursor-not-allowed cursor-pointer">
                                            Learn More
                                        </Link>
                                    ) : (
                                        <button
                                            disabled
                                            className="cursor-not-allowed text-gray-400">
                                            Learn More
                                        </button>
                                    )}
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </div>
        </>
    );
}

export default Portfolio;